"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Image from "next/image";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetClose,
} from "@/components/sheet/sheet";
import { Button } from "@/components/button/button/button";
import { X } from "lucide-react";
import { toCmsHref } from "@/lib/cmsHelpers";
import styles from "./Navbar.module.css";

type NavLink = {
  id?: number;
  label: string;
  href: string;
  newTab?: boolean;
  icon?: string | null;
  order?: number;
};

type CTAButton = {
  id?: number;
  copy: string;
  title: string;
  url: string;
  type: string;
  target: string;
  button_customID?: string;
  size?: string;
};

interface LogoSource {
  url: string;
  width?: number;
  height?: number;
}

interface NavbarProps {
  navL1?: NavLink[];
  ctaButtons?: CTAButton[];
  logoLight?: LogoSource;
  logoDark?: LogoSource;
}

const isSvgSrc = (src: string | undefined): boolean => {
  const s = String(src || "").trim().toLowerCase();
  if (!s) return false;
  if (s.startsWith("data:image/svg+xml")) return true;
  // Strip query/hash for extension check
  const noQuery = s.split("?")[0].split("#")[0];
  return noQuery.endsWith(".svg");
};

const Navbar = ({ navL1, ctaButtons, logoLight, logoDark }: NavbarProps) => {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isDark, setIsDark] = React.useState(false);
  const pathname = usePathname();

  // Handle hash link clicks — scroll to section if already on the target page
  const handleHashClick = (e: React.MouseEvent, href: string) => {
    const hashIndex = href.indexOf("#");
    if (hashIndex === -1) return; // not a hash link, let Next.js handle it

    const targetPath = href.slice(0, hashIndex) || "/";
    const hash = href.slice(hashIndex);

    // If we're already on the target page, scroll to the element directly
    if (pathname === targetPath) {
      e.preventDefault();
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
      // Update URL hash without navigation
      window.history.pushState(null, "", href);
    }
    // Otherwise let Next.js navigate; the browser will handle the hash scroll on load
  };

  const resolveHref = (href: string): string => {
    if (!href) return href;
    // If this is just a hash (e.g. "#features"), send the user to the homepage section.
    if (href.startsWith("#")) {
      return `/${href}`;
    }
    // Normalize CMS-stored values: editors may enter `about` or `/about` —
    // `<Link>` only treats leading-slash paths as absolute, so coerce.
    return toCmsHref(href) || href;
  };

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    const handleThemeChange = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    // Initial theme check
    handleThemeChange();

    // Set up observers
    window.addEventListener('scroll', handleScroll);

    // Watch for theme changes
    const observer = new MutationObserver(handleThemeChange);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  // Resolve logo descriptors with fallbacks. Width/height fall back to
  // the navbar's design-intent dimensions (128 × 48) when the source
  // doesn't ship its own — this matches the local SVG fallbacks. When
  // the CMS provides natural dimensions (Cloudinary uploads), we use
  // those so the declared aspect ratio matches the served image.
  const lightLogo: LogoSource = logoLight ?? { url: "/logo/logo-lightmode.svg" };
  const darkLogo: LogoSource = logoDark ?? { url: "/logo/logo-darkmode.svg" };
  const lightSrc = lightLogo.url;
  const darkSrc = darkLogo.url;
  const lightWidth = lightLogo.width ?? 128;
  const lightHeight = lightLogo.height ?? 48;
  const darkWidth = darkLogo.width ?? 128;
  const darkHeight = darkLogo.height ?? 48;

  return (
    <header className={`${styles.header} ${styles.headerMount}`}>
      {/* Full-width background bar */}
      <div className={`${styles['header-background']} ${
        !isScrolled
          ? `${styles['header-background-visible']} ${isDark ? styles['header-background-dark'] : ''}`
          : styles['header-background-hidden']
      }`} />

      <div className={`${styles.navbar} ${
        isScrolled
          ? `${styles['navbar-scrolled']} ${isDark ? styles['navbar-scrolled-dark'] : ''}`
          : `${styles['navbar-default']} ${isDark ? styles['navbar-default-dark'] : ''}`
      }`}>
        {/* Dark theme background wrapper - inside navbar */}
        {isDark && (
          <div className={styles['dark-bg']} />
        )}
        {/* Logo - Left Side */}
        <div className={styles['logo-section']}>
          <Link href="/" className={styles['logo-link']}>
            <div className={styles['logo-container']}>
              {/* Light mode logo — declared dims come from the CMS media
                  doc when available so HTML attrs match the served image
                  aspect, otherwise fall back to the design-intent 128×48. */}
              <Image
                src={lightSrc}
                alt="Logo"
                width={lightWidth}
                height={lightHeight}
                priority
                fetchPriority="high"
                unoptimized={isSvgSrc(lightSrc)}
                className={`${styles['logo-image']} ${styles['logo-light']}`}
              />
              {/* Dark mode logo */}
              <Image
                src={darkSrc}
                alt="Logo"
                width={darkWidth}
                height={darkHeight}
                priority
                fetchPriority="high"
                unoptimized={isSvgSrc(darkSrc)}
                className={`${styles['logo-image']} ${styles['logo-dark']}`}
              />
            </div>
          </Link>
        </div>

        {/* Desktop Nav - Center */}
        <div className={styles['nav-section']}>
          <nav className={styles['nav-desktop']}>
            {(navL1 && navL1.length > 0
              ? navL1
              : [
                  { label: "Features", href: "/#features" },
                  { label: "FAQs", href: "/#faq" },
                ]
            ).map((link) => (
              <Link
                key={link.id ?? link.href ?? link.label}
                href={resolveHref(link.href)}
                className={styles['nav-link']}
                scroll={!link.href.includes("#")}
                onClick={(e) => handleHashClick(e, resolveHref(link.href))}
                target={link.newTab ? "_blank" : undefined}
                rel={link.newTab ? "noopener noreferrer" : undefined}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* CTA Buttons - Right Side */}
        <div className={styles['cta-section']}>
          {/* Desktop CTA Buttons */}
          <div className={styles['cta-desktop']}>
            {ctaButtons && ctaButtons.length > 0 && ctaButtons.map((btn) => {
              const customId = btn.button_customID?.toLowerCase().trim();
              const isDownloadButton = customId === "download";
              const isEmailLinkButton = customId === "email";
              const isPrimary = btn.type === "primary";
              const rawHref = (btn.url || "").trim();
              const emailHref =
                isEmailLinkButton && rawHref
                  ? rawHref.toLowerCase().startsWith("mailto:")
                    ? rawHref
                    : `mailto:${rawHref}`
                  : "";

              const downloadIcon = isDownloadButton ? (
                <Image
                  src="/misc/apple-logo.svg"
                  alt=""
                  width={14}
                  height={17}
                  className={styles['cta-icon']}
                  aria-hidden="true"
                />
              ) : null;

              // Non-primary buttons render as plain nav links (like "Sign In" on main)
              if (!isPrimary) {
                const href = isEmailLinkButton && emailHref ? emailHref : (btn.url || "#");
                const isBlank = btn.target === "blank";
                return (
                  <Link
                    key={btn.id ?? btn.url}
                    href={href}
                    target={isBlank ? "_blank" : undefined}
                    rel={isBlank ? "noopener noreferrer" : undefined}
                    className={styles['nav-link']}
                  >
                    {btn.copy}
                  </Link>
                );
              }

              // Primary button variant changes on scroll: subtle at top, filled when scrolled
              const variant = isScrolled ? "primary" as const : "default" as const;

              // Primary CTA: navigate using CMS-provided URL
              return (
                <Button
                  key={btn.id ?? btn.url}
                  asChild
                  variant={variant}
                  size="md"
                >
                  <Link
                    href={btn.url}
                    target={btn.target === "blank" ? "_blank" : undefined}
                    rel={
                      btn.target === "blank"
                        ? "noopener noreferrer"
                        : undefined
                    }
                  >
                    {downloadIcon}
                    <span>{btn.copy}</span>
                  </Link>
                </Button>
              );
            })}
          </div>

          {/* Mobile Menu Button */}
          <div className={styles['mobile-menu-wrap']}>
          <Sheet>
            <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className={styles['hamburger-trigger']}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles['hamburger-icon']}>
                  <line x1="4" x2="20" y1="12" y2="12" />
                  <line x1="4" x2="20" y1="6" y2="6" />
                  <line x1="4" x2="20" y1="18" y2="18" />
                </svg>
                <span className={styles['sr-only']}>Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              hideClose
              className={styles['sheet-content']}
            >
              <SheetTitle className={styles['sr-only']}>Navigation Menu</SheetTitle>

              {/* Custom header with close button on left */}
              <div className={styles['sheet-close-row']}>
                <SheetClose asChild>
                  <Button variant="ghost" size="icon" className={styles['sheet-close-btn']}>
                    <X className={styles['sheet-close-icon']} />
                    <span className={styles['sr-only']}>Close menu</span>
                  </Button>
                </SheetClose>
              </div>

              <div className={styles['mobile-nav']}>
                {(navL1 && navL1.length > 0
                  ? navL1
                  : [
                      { label: "Features", href: "/#features" },
                      { label: "FAQs", href: "/#faq" },
                    ]
                ).map((link) => (
                  <div className={styles['mobile-nav-item']} key={link.id ?? link.href ?? link.label}>
                    <SheetClose asChild>
                      <Link
                        href={resolveHref(link.href)}
                        className={styles['mobile-nav-link']}
                        scroll={!link.href.includes("#")}
                        onClick={(e) => handleHashClick(e, resolveHref(link.href))}
                        target={link.newTab ? "_blank" : undefined}
                        rel={link.newTab ? "noopener noreferrer" : undefined}
                      >
                        {link.label}
                      </Link>
                    </SheetClose>
                  </div>
                ))}

                <div className={styles['mobile-cta-group']}>
                  {/* CTA buttons — primary renders first in the flyout so
                      "Sign in" (or any secondary/ghost CTA) sits beneath
                      the main action, regardless of CMS authoring order. */}
                  {ctaButtons && ctaButtons.length > 0 && [...ctaButtons]
                    .sort((a, b) => {
                      const ap = a.type === "primary" ? 0 : 1;
                      const bp = b.type === "primary" ? 0 : 1;
                      return ap - bp;
                    })
                    .map((btn) => {
                    const customId = btn.button_customID?.toLowerCase().trim();
                    const isDownloadButton = customId === "download";
                    const isEmailLinkButton = customId === "email";
                    const rawHref = (btn.url || "").trim();
                    const emailHref =
                      isEmailLinkButton && rawHref
                        ? rawHref.toLowerCase().startsWith("mailto:")
                          ? rawHref
                          : `mailto:${rawHref}`
                        : "";
                    return (
                      <div className={styles['mobile-nav-item']} key={btn.id ?? btn.url}>
                        <SheetClose asChild>
                          {isEmailLinkButton && emailHref ? (
                            <Button
                              asChild
                              className={styles['mobile-cta-button']}
                              variant={
                                btn.type === "primary"
                                  ? "primary"
                                  : btn.type === "secondary"
                                  ? "default"
                                  : "ghost"
                              }
                              size="lg"
                            >
                              <a href={emailHref}>
                                {isDownloadButton && (
                                  <Image
                                    src="/misc/apple-logo.svg"
                                    alt=""
                                    width={14}
                                    height={17}
                                    className={styles['mobile-cta-icon']}
                                    aria-hidden="true"
                                  />
                                )}
                                <span>{btn.copy}</span>
                              </a>
                            </Button>
                          ) : (
                            <Button
                              asChild
                              className={styles['mobile-cta-button']}
                              variant={
                                btn.type === "primary"
                                  ? "primary"
                                  : btn.type === "secondary"
                                  ? "default"
                                  : "ghost"
                              }
                              size="lg"
                            >
                      <Link
                                href={btn.url}
                                target={
                                  btn.target === "blank" ? "_blank" : undefined
                                }
                                rel={
                                  btn.target === "blank"
                                    ? "noopener noreferrer"
                                    : undefined
                                }
                              >
                                {isDownloadButton && (
                                  <Image
                                    src="/misc/apple-logo.svg"
                                    alt=""
                                    width={14}
                                    height={17}
                                    className={styles['mobile-cta-icon']}
                                    aria-hidden="true"
                                  />
                                )}
                                <span>{btn.copy}</span>
                      </Link>
                    </Button>
                          )}
                        </SheetClose>
                  </div>
                    );
                  })}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      </div>
    </header>
  );
};

export default Navbar;
